import { useMemo } from "react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePrefsStore, type DateFormat } from "@/stores/prefs.store";
import { cn } from "@/lib/utils";

const TIMEZONES = [
  "UTC",
  "Europe/Paris",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Australia/Sydney",
];

export function PreferencesSection() {
  const timezone = usePrefsStore((s) => s.timezone);
  const setTimezone = usePrefsStore((s) => s.setTimezone);
  const dateFormat = usePrefsStore((s) => s.date_format);
  const setDateFormat = usePrefsStore((s) => s.setDateFormat);
  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);

  const tzOptions = TIMEZONES.includes(timezone)
    ? TIMEZONES
    : [timezone, ...TIMEZONES];

  return (
    <section className="rounded-plane bg-white p-6 shadow-plane">
      <h2 className="text-lg font-medium tracking-tight text-slate-900">
        Preferences
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Personal display settings, stored locally on this device
      </p>

      <div className="mt-5 space-y-6">
        <Field label="Timezone">
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger className="h-10 max-w-sm rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tzOptions.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Date format">
          <DateFormatToggle value={dateFormat} onChange={setDateFormat} />
        </Field>

        <Field label="Language">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="h-10 max-w-sm rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-slate-400">
            More languages coming later
          </p>
        </Field>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function DateFormatToggle({
  value,
  onChange,
}: {
  value: DateFormat;
  onChange: (v: DateFormat) => void;
}) {
  // Live preview using a fixed reference timestamp ~2 hours ago so the
  // "Relative" example reads as expected without depending on the user's
  // current clock minute.
  const example = useMemo(() => {
    const ref = new Date(Date.now() - 2 * 60 * 60 * 1000);
    return value === "relative"
      ? "2 hours ago"
      : format(ref, "MMM d, yyyy HH:mm");
  }, [value]);

  return (
    <div>
      <div
        role="radiogroup"
        aria-label="Date format"
        className="inline-flex h-10 rounded-lg border border-slate-200 bg-slate-50 p-1"
      >
        <ToggleButton
          active={value === "relative"}
          onClick={() => onChange("relative")}
        >
          Relative
        </ToggleButton>
        <ToggleButton
          active={value === "absolute"}
          onClick={() => onChange("absolute")}
        >
          Absolute
        </ToggleButton>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Dates will display as{" "}
        <span className="font-medium text-slate-700">{example}</span>
      </p>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "flex h-8 w-28 items-center justify-center rounded-md text-sm font-medium transition-colors",
        active
          ? "bg-indigo-600 text-white shadow-sm-tile"
          : "text-slate-600 hover:text-slate-900",
      )}
    >
      {children}
    </button>
  );
}
